import type { StaticImageData } from 'next/image';
import { useTranslations } from 'next-intl';
import bayLines from './assets/bay-lines.svg';
import carCompactWhite from './assets/car-compact-white.webp';
import carMediumDgreen from './assets/car-medium-dgreen.webp';
import carMediumGrey from './assets/car-medium-grey.webp';
import carMediumRed from './assets/car-medium-red.webp';
import carSmallBlue from './assets/car-small-blue.webp';
import carVanDblue from './assets/car-van-dblue.webp';
import logoAlamo from './assets/logo-alamo.svg';
import logoAvis from './assets/logo-avis.svg';
import logoBudget from './assets/logo-budget.svg';
import logoEnterprise from './assets/logo-enterprise.svg';
import logoEuropcar from './assets/logo-europcar.svg';
import logoHertz from './assets/logo-hertz.svg';
import {
  Bay,
  BayLines,
  Car,
  CarPark,
  CarParkIntro,
  Logo,
  LogoRow,
  ParkingSpace,
} from './HomeCarPark.styles';
import type { BayVisibility } from './HomeCarPark.styles';

type ParkingBay = {
  car: StaticImageData;
  company: string;
  /** The white logo variant from the design system, for the green stage. */
  logo: StaticImageData;
  /** The logo width in the design, where a bay is 112px wide. */
  logoWidth: number;
  visibleFrom: BayVisibility;
};

/** The order and the breakpoints follow the final Figma design. */
const bays: ParkingBay[] = [
  {
    company: 'Budget',
    car: carSmallBlue,
    logo: logoBudget,
    logoWidth: 66,
    visibleFrom: 'lg',
  },
  {
    company: 'Enterprise',
    car: carMediumGrey,
    logo: logoEnterprise,
    logoWidth: 87,
    visibleFrom: 'md',
  },
  {
    company: 'Europcar',
    car: carMediumDgreen,
    logo: logoEuropcar,
    logoWidth: 59,
    visibleFrom: 'xs',
  },
  {
    company: 'Hertz',
    car: carCompactWhite,
    logo: logoHertz,
    logoWidth: 41,
    visibleFrom: 'xs',
  },
  {
    company: 'Avis',
    car: carMediumRed,
    logo: logoAvis,
    logoWidth: 44,
    visibleFrom: 'xs',
  },
  {
    company: 'Alamo',
    car: carVanDblue,
    logo: logoAlamo,
    logoWidth: 40,
    visibleFrom: 'lg',
  },
];

/**
 * A car park seen from above, with the logo of a rental company over each
 * bay. Screen readers read a list of the rental companies after a visually
 * hidden introduction. Each logo has the company name as alternative text.
 * The cars and the bay lines are decorative.
 * xs shows 3 bays, md shows 4 bays and lg shows 6 bays. Hidden bays are not
 * in the list for screen readers either.
 */
const HomeCarPark = () => {
  const t = useTranslations('features.stage');

  return (
    <>
      <CarParkIntro>{t('homeFirstViewport.carParkIntro')}</CarParkIntro>
      <CarPark data-testid="home-car-park">
        {bays.map(({ car, company, logo, logoWidth, visibleFrom }) => (
          <Bay key={company} visibleFrom={visibleFrom} data-company={company}>
            <LogoRow>
              <Logo
                src={logo}
                alt={company}
                unoptimized
                $designWidth={logoWidth}
              />
            </LogoRow>
            <ParkingSpace>
              <BayLines src={bayLines} alt="" unoptimized />
              <Car src={car} alt="" loading="eager" unoptimized />
              <BayLines src={bayLines} alt="" unoptimized $flipped />
            </ParkingSpace>
          </Bay>
        ))}
      </CarPark>
    </>
  );
};

export { HomeCarPark };
